import { useState } from "react";
import { useTranslation } from "react-i18next";
import { User } from "firebase/auth";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { card, widePageContainer, primaryBtn } from "../styles";
import type { AuthModalMode } from "../components/AuthModal";

const FAQS = [
  {
    q: "What is Scoracle?",
    a: "Scoracle is a fantasy sport prediction platform. Create private leagues with friends and compete across a range of game modes, from tracking goals scored by your picked players to predicting final league standings. All games are based on real world events, so you can track your league in real time.",
  },
  {
    q: "Is Scoracle free to use?",
    a: "Yes, completely free. There are no subscriptions, no premium tiers, and no in-app purchases. We run Scoracle as a passion project and cover hosting costs ourselves.",
  },
  {
    q: "What game modes are available?",
    a: "Scoracle supports a range of game modes: Target, Final Positions, Gameweek Challenge, Syndicate, and Predict the Score, and we are always looking at adding more. Create a league to find out more about each game mode.",
  },
  {
    q: "How do I create a league?",
    a: "Sign in, go to the Overview page, and click \"Create a League\". A short wizard walks you through three steps: pick a game mode (tap a row to see its description and winning condition), choose a competition and any optional settings (like a goal target or starting gameweek), then review your choices and hit Create. Share the invite code with your friends to get them in.",
  },
  {
    q: "Why can't I create some game modes right now?",
    a: "A game mode is only available if there is an open, compatible competition to attach it to. This can mean a few things: no competitions are currently running (off-season), the competitions that are running do not support that particular mode, that mode's join window for this competition has already closed, or — for Syndicate specifically — no pots have been set up on it yet.",
    id: "mode-availability",
  },
  {
    q: "How does Syndicate work?",
    a: "The competition admin builds a board of \"pots\" — each one either a club (scored on wins and draws) or a player (scored per goal, assist, yellow card, or red card). You pick one club or player from every pot before the league starts. Picks lock at kickoff and can't be changed — a player who stops featuring simply stops scoring. Note that the admin can bar specific players from a pot (to stop the obvious pick being universal), so not every player in the competition is necessarily available for every pot.",
  },
  {
    q: "How do I join a league?",
    a: "From the Overview page, click \"Join a League\" and enter the league code.",
  },
  {
    q: "How is scoring calculated?",
    a: "It depends on the game mode. Find out more when you are creating a league, or once you have joined.",
  },
  {
    q: "Can a gameweek's results change after it has finished?",
    a: "Occasionally, yes. Results are taken from official match data as soon as a gameweek finishes, but the data provider sometimes makes corrections shortly afterwards — for example when a goal or assist is credited to a different player, or a card is rescinded. When that happens, scores update automatically, so a leaderboard can shift slightly after a gameweek has been marked complete. Corrections are usually small and settle within a day or so.",
  },
  {
    q: "Do I need an account to play?",
    a: "No. If someone in your group does not want to sign up, the league admin can add them as a guest. The admin manages the guest's team selections and predictions on their behalf. That said, signing up gives a player full control of their own team, access to their personal stats and score history, and the ability to manage their team themselves.",
  },
  {
    q: "Can other players see my picks before the competition starts?",
    a: "No. Whatever you pick — a squad, a table prediction, a score prediction — stays private to you until it locks: when the league starts, or for Predict the Score, until that fixture kicks off. This stops anyone copying your picks before they count. The one exception is guest players: since the league admin manages a guest's team on their behalf, the admin can see and edit that guest's picks pre-lock too.",
  },
  {
    q: "Can a site admin change my picks after I've made them?",
    a: "In exceptional cases, yes. A site admin can correct a member's picks mid-season — for example, to fix a mis-entered squad or resolve a genuine dispute. This isn't something we expect to do routinely, and it isn't limited by the league's start date: it can happen whether the league is still preparing, already running, or finished. If your picks ever change unexpectedly, contact support@scoracle.cc.",
  },
  {
    q: "Why has a finished league disappeared from my active leagues?",
    a: "It hasn't. Finished leagues stay visible in your active leagues even after the competition ends. Once the site admin decides the competition is no longer current, they archive it, at which point it moves to the Completed tab. You can always find past leagues under Completed.",
  },
  {
    q: "How do I contact support?",
    a: "Email us at support@scoracle.cc. We aim to respond within a few days.",
  },
  {
    q: "How is my data handled?",
    a: "Scoracle stores your email address and display name, along with any league activity, team picks, and predictions you create. We do not sell or share your personal data with third parties. Data is retained for as long as your account is active. If you arrive via a marketing link (for example one with a utm_source parameter) or from another website, we record that in your browser's session storage and attach it to your account once on sign-up, so we can see which channels bring in new players — this is cleared when you close your browser tab and is never used for cross-site tracking.",
  },
  {
    q: "Can I delete my account or data?",
    a: "Yes. Email support@scoracle.cc with a deletion request and we will remove your account and all associated data within 30 days.",
  },
];

const STORY_STEPS = [
  {
    color: "var(--color-mode-syndicate)",
    title: "It started with a group chat",
    body: "A few of us kept arguing over who had better football knowledge. We designed a game called Target to settle it, using a scrappy spreadsheet to keep scores.",
  },
  {
    color: "var(--color-mode-target)",
    title: "Real matches, real drama",
    body: "Every league scores off actual results as they happen. Real goals, real tables, real bragging rights, no simulations.",
  },
  {
    color: "var(--color-mode-final-positions)",
    title: "One game became many",
    body: "Target wasn't enough. We kept adding formats, each one born from a different argument about who's actually good at football.",
  },
  {
    color: "var(--color-mode-gameweek-challenge)",
    title: "We built a site",
    body: "So we built this to replace the spreadsheet entirely, just in time for World Cup 2026.",
  },
  {
    color: "var(--color-mode-score-predictor)",
    title: "Now we are opening it up for everyone",
    body: "We have made this site available to all sports fans and friend groups around the world, completely free.",
  },
];

function StoryTimeline() {
  return (
    <div>
      {STORY_STEPS.map((step, i) => (
        <div key={step.title} style={{ display: "flex", gap: "1.25rem" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
            <div style={{
              width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "0.8rem", fontWeight: 700,
              background: step.color, color: "var(--color-btn-primary-text)",
            }}>
              {i + 1}
            </div>
            {i < STORY_STEPS.length - 1 && (
              <div style={{ width: 2, flex: 1, background: "var(--color-border)", marginTop: "0.35rem" }} />
            )}
          </div>
          <div style={{ paddingBottom: i < STORY_STEPS.length - 1 ? "1.75rem" : 0 }}>
            <h3 style={{ margin: "0.15rem 0 0.4rem", fontSize: "1.05rem", fontWeight: 700, color: step.color }}>
              {step.title}
            </h3>
            <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--color-text-muted)", lineHeight: 1.6, maxWidth: 560 }}>
              {step.body}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function Accordion({ faqs }: { faqs: typeof FAQS }) {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      {faqs.map((faq, i) => (
        <div
          key={i}
          id={"id" in faq ? (faq as any).id : undefined}
          style={{ ...card, padding: 0, overflow: "hidden", borderLeft: "3px solid var(--color-accent)" }}
        >
          <button
            onClick={() => setOpen(open === i ? null : i)}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "1rem",
              padding: "0.85rem 1.25rem",
              background: "none",
              border: "none",
              cursor: "pointer",
              textAlign: "left",
              fontFamily: "var(--font-body)",
            }}
          >
            <span style={{ fontSize: "0.93rem", fontWeight: 600, color: "var(--color-text)" }}>
              {faq.q}
            </span>
            <svg
              width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="var(--color-text-muted)" strokeWidth="2"
              style={{ flexShrink: 0, transition: "transform 0.2s", transform: open === i ? "rotate(180deg)" : "rotate(0deg)" }}
            >
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </button>
          <div style={{
            display: "grid",
            gridTemplateRows: open === i ? "1fr" : "0fr",
            transition: "grid-template-rows 0.25s ease",
          }}>
            <div style={{ overflow: "hidden" }}>
              <p style={{
                margin: 0,
                padding: "0 1.25rem 1rem",
                fontSize: "0.88rem",
                color: "var(--color-text-muted)",
                lineHeight: 1.65,
              }}>
                {faq.a}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

interface Props {
  user?: User | null;
  onSignIn?: (mode?: AuthModalMode) => void;
}

export default function AboutPage({ user, onSignIn }: Props) {
  useBreadcrumbs([{ label: "About" }]);
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  return (
    <div className="sg-fade-in" style={{ ...widePageContainer, padding: "1.5rem 0" }}>

      {/* ── Hero ── */}
      <section style={{ marginBottom: "2.5rem" }}>
        <div style={{
          borderRadius: 20,
          padding: isMobile ? "2rem 1.5rem" : "3rem 3.5rem",
          background: "var(--color-surface-alt)",
          border: "1px solid var(--color-border-subtle)",
        }}>
          <div style={{
            fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase",
            letterSpacing: "0.12em", color: "var(--color-text-dim)", marginBottom: "0.9rem",
          }}>
            The story behind Scoracle
          </div>
          <h1 style={{
            margin: "0 0 1.1rem",
            fontFamily: "var(--font-display)",
            fontSize: isMobile ? "1.9rem" : "2.6rem",
            fontWeight: 800,
            color: "var(--color-text)",
            lineHeight: 1.15,
          }}>
            Built by fans,<br />
            settled by <span style={{ color: "var(--color-mode-syndicate)" }}>real</span>{" "}
            <span style={{ color: "var(--color-mode-score-predictor)" }}>football.</span>
          </h1>
          <p style={{ margin: 0, fontSize: "1rem", color: "var(--color-text-muted)", lineHeight: 1.7, maxWidth: 620 }}>
            No simulations, no dice rolls. Every league on Scoracle scores off actual matches as they're played.
            We built it to end friendly arguments. It got a little out of hand.
          </p>
          {!user && (
            <button
              onClick={() => onSignIn?.("register")}
              style={{ ...primaryBtn, marginTop: "1.75rem" }}
            >
              {t("landing.header.playFree")} →
            </button>
          )}
        </div>
      </section>

      {/* ── How it came together ── */}
      <section style={{ marginBottom: "2.5rem" }}>
        <h2 style={{
          margin: "0 0 1.25rem",
          fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase",
          letterSpacing: "0.12em", color: "var(--color-text-dim)",
        }}>
          How it came together
        </h2>
        <StoryTimeline />
      </section>

      {/* ── FAQ ── */}
      <section style={{ marginBottom: "2.5rem" }}>
        <h2 style={{
          margin: "0 0 1rem",
          fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase",
          letterSpacing: "0.12em", color: "var(--color-text-dim)",
        }}>
          FAQ
        </h2>
        <Accordion faqs={FAQS} />
      </section>

      {!user && (
        <div style={{
          background: "var(--color-accent)",
          borderRadius: 20,
          padding: isMobile ? "2.5rem 1.5rem" : "3.5rem 2rem",
          textAlign: "center",
        }}>
          <h2 style={{
            margin: "0 0 0.75rem",
            fontFamily: "var(--font-display)",
            fontSize: isMobile ? "1.7rem" : "2.2rem",
            fontWeight: 800,
            color: "var(--color-btn-primary-text)",
          }}>
            {t("landing.finalCta.heading")}
          </h2>
          <p style={{
            margin: "0 auto 1.75rem",
            fontSize: isMobile ? "0.9rem" : "1rem",
            color: "var(--color-btn-primary-text)",
            opacity: 0.85,
            maxWidth: 440,
            lineHeight: 1.5,
          }}>
            {t("landing.finalCta.subtitle")}
          </p>
          <button
            onClick={() => onSignIn?.("register")}
            style={{
              ...primaryBtn,
              background: "var(--color-btn-primary-text)",
              color: "var(--color-accent)",
              fontSize: "1rem",
              padding: "0.75rem 1.9rem",
            }}
          >
            {t("landing.finalCta.cta")} →
          </button>
        </div>
      )}

    </div>
  );
}
